import React, { useState, useEffect, useCallback } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { db } from '../config/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';

const Takvim = () => {
  const [date, setDate] = useState(new Date());
  const [workHours, setWorkHours] = useState({});
  const [weeklyWorkHours, setWeeklyWorkHours] = useState({});
  const [totalHours, setTotalHours] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);

  const formatDateToLocalString = useCallback((date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const getStartOfWeek = useCallback((date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    return startOfWeek;
  }, []);

  const getEndOfWeek = useCallback((date) => {
    const endOfWeek = new Date(date);
    endOfWeek.setDate(date.getDate() + (6 - date.getDay()));
    return endOfWeek;
  }, []);

  const fetchWorkHours = useCallback(async () => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const q = query(
      collection(db, 'workHours'),
      where('date', '>=', formatDateToLocalString(startOfMonth)),
      where('date', '<=', formatDateToLocalString(endOfMonth))
    );

    const querySnapshot = await getDocs(q);
    const hoursData = {};
    let totalHours = 0;
    let totalMinutes = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const start = new Date(`1970-01-01T${data.startTime}:00`);
      const end = new Date(`1970-01-01T${data.endTime}:00`);
      const minutes = (end - start) / (1000 * 60);
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;

      hoursData[data.date] = {
        startTime: data.startTime,
        endTime: data.endTime,
        hours: minutes / 60,
      };

      totalHours += hours;
      totalMinutes += mins;
    });

    totalHours += Math.floor(totalMinutes / 60);
    totalMinutes = totalMinutes % 60;

    setWorkHours(hoursData);
    setTotalHours(totalHours);
    setTotalMinutes(totalMinutes);
  }, [date, formatDateToLocalString]);

  const fetchWeeklyWorkHours = useCallback(async () => {
    const startOfWeek = getStartOfWeek(date);
    const endOfWeek = getEndOfWeek(date);

    const q = query(
      collection(db, 'workHours'),
      where('date', '>=', formatDateToLocalString(startOfWeek)),
      where('date', '<=', formatDateToLocalString(endOfWeek))
    );

    const querySnapshot = await getDocs(q);
    const weeklyData = {};

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const dateStr = data.date;

      const start = new Date(`1970-01-01T${data.startTime}:00`);
      const end = new Date(`1970-01-01T${data.endTime}:00`);
      const hours = (end - start) / (1000 * 60 * 60);

      weeklyData[dateStr] = {
        startTime: data.startTime,
        endTime: data.endTime,
        hours,
      };
    });

    setWeeklyWorkHours(weeklyData);
  }, [date, formatDateToLocalString, getStartOfWeek, getEndOfWeek]);

  useEffect(() => {
    fetchWorkHours();
    fetchWeeklyWorkHours();
  }, [fetchWorkHours, fetchWeeklyWorkHours]);

  const handleMonthChange = (newDate) => {
    setDate(newDate);
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dateStr = formatDateToLocalString(date);
      if (workHours[dateStr]) {
        return (
          <div className="text-center text-muted small">
            {workHours[dateStr].startTime} - {workHours[dateStr].endTime}
          </div>
        );
      }
    }
    return null;
  };

  const getWeekdayName = (dayIndex) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[dayIndex];
  };

  const getWeeklyHours = () => {
    const startOfWeek = getStartOfWeek(date);
    const weeklyHours = [];

    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(currentDay.getDate() + i);
      const dayName = getWeekdayName(i);
      const dateStr = formatDateToLocalString(currentDay);

      const hours = weeklyWorkHours[dateStr]?.hours || 0;
      const totalMinutes = Math.round(hours * 60);
      const displayHours = Math.floor(totalMinutes / 60);
      const displayMinutes = totalMinutes % 60;

      weeklyHours.push({
        day: dayName,
        hours: displayHours,
        minutes: displayMinutes
      });
    }

    return weeklyHours;
  };

  return (
    <div className="container mt-3">
      {/* Total Monthly Hours Card */}
      <div className="card mb-4 shadow-sm border-0 rounded-lg">
        <div className="card-body text-center py-4">
          <h4 className="font-weight-bold mb-3">
            <i className="bi bi-clock-history me-2"></i>
            Monthly Freelance Work Summary for Little Hibba
          </h4>
          <div className="d-flex justify-content-center align-items-center">
            <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
              <i className="bi bi-clock text-primary fs-4"></i>
            </div>
            <div>
              <h2 className="mb-0 text-primary">
                {totalHours} <small className="text-muted fs-6">hours</small> {totalMinutes} <small className="text-muted fs-6">minutes</small>
              </h2>
              <p className="text-muted mb-0">Total worked this month</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Left Column: Monthly Calendar */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100 shadow-sm border-0 rounded-lg">
            <div className="card-header bg-white border-0 py-3">
              <h5 className="mb-0">
                <i className="bi bi-calendar3 me-2"></i>
                Monthly Work Calendar
              </h5>
            </div>
            <div className="card-body">
              <Calendar
                onChange={handleMonthChange}
                value={date}
                view="month"
                tileContent={tileContent}
                className="w-100 border-0"
              />
            </div>
          </div>
        </div>

        {/* Right Column: Weekly Work Hours */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100 shadow-sm border-0 rounded-lg">
            <div className="card-header bg-white border-0 py-3">
              <h5 className="mb-0">
                <i className="bi bi-calendar-week me-2"></i>
                Weekly Work Hours ({formatDateToLocalString(getStartOfWeek(date))} - {formatDateToLocalString(getEndOfWeek(date))})
              </h5>
            </div>
            <div className="card-body">
              <ul className="list-group">
                {getWeeklyHours().map((item) => (
                  <li key={item.day} className="list-group-item d-flex justify-content-between">
                    <span>{item.day}</span>
                    <span>
                      {item.hours} hours{item.minutes > 0 ? ` ${item.minutes} minutes` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Takvim;